import { Box, IconButton } from '@mui/material';
import React, { useRef } from 'react';
import { Cancel, FormatAlignLeft, Sync } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { merge } from 'lodash';
import { Editor } from '@monaco-editor/react';
import CustomTooltip from '../../atoms/CustomTooltip';
import ErrorFeedbackP from '../../atoms/ErrorFeedbackP';
import { useValidator } from './hooks/useValidator';

const CustomEditor = ({
  errors,
  editable,
  fetchable,
  isFetching,
  handleFetch,
  disabled,
  validatorStrategy,
  onChange,
  validationSchema,
  ...editorProps
}) => {
  const editorRef = useRef();
  const { t } = useTranslation();

  const {
    errors: validationErrors,
    setErrors,
    handleValidate,
  } = useValidator({ strategy: validatorStrategy, schema: validationSchema });

  const allErrors = [...errors, ...validationErrors];

  const handleEditorWillMount = monaco => {
    monaco.editor.defineTheme(editorProps?.theme ?? 'light', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#2C3E50',
      },
    });
  };

  const handleEditorDidMount = editor => {
    if (editorRef) editorRef.current = editor;
  };

  const clear = () => {
    editorRef.current?.setValue?.(editorProps?.defaultValue ?? '');
    setErrors([]);
  };

  const format = () => {
    if (editorRef.current) {
      editorRef.current.getAction('editor.action.formatDocument').run();
    }
  };

  return (
    <Box position="relative">
      <Box
        sx={{
          background: '#2C3E50',
          padding: '1rem 0',
          borderRadius: '6px',
          '.monaco-editor': {
            outline: 0,
          },
        }}
      >
        <Editor
          height={editable ? 360 : 25}
          loading={t('components.custom_editor.loading')}
          beforeMount={handleEditorWillMount}
          onMount={handleEditorDidMount}
          {...editorProps}
          options={merge(
            {
              readOnly: !editable || disabled,
              readOnlyMessage: {
                value: t('components.custom_editor.view_only'),
              },
              renderLineHighlight: 'none',

              tabSize: 2,
              fontFamily: 'monospace',
              fontSize: '16px',
              minimap: {
                enabled: false,
              },
              lineNumbers: 'off',
              stickyScroll: {
                enabled: false,
              },
            },
            editorProps?.options ?? {},
          )}
          onChange={data => onChange(handleValidate(data))}
        />
      </Box>
      {allErrors.map(error => (
        <ErrorFeedbackP
          key={`${error.instancePath}-${error.keyword}-${JSON.stringify(
            error.params,
          )}`}
          touched
          error={
            error.schemaPath
              ? t('components.custom_editor.error_in', {
                  field: error.instancePath || '/',
                  message: error.message,
                })
              : error.message
          }
        />
      ))}

      {editable && (
        <Box position="absolute" right={10} top={10}>
          <CustomTooltip title={t('components.custom_editor.format')}>
            <IconButton
              sx={{ color: 'white' }}
              onClick={format}
              disabled={disabled}
            >
              <FormatAlignLeft />
            </IconButton>
          </CustomTooltip>
          <CustomTooltip title={t('components.custom_editor.clear')}>
            <IconButton
              sx={{ color: 'white' }}
              onClick={clear}
              disabled={disabled}
            >
              <Cancel />
            </IconButton>
          </CustomTooltip>
        </Box>
      )}

      {fetchable && (
        <CustomTooltip title={t('components.custom_editor.sync')}>
          <IconButton
            disabled={disabled}
            onClick={handleFetch}
            sx={{
              position: 'absolute',
              right: 10,
              top: 10,
              color: 'white',
              animation: isFetching && 'spin 1s linear infinite reverse',
              '@keyframes spin': isFetching && {
                '0%': {
                  transform: 'rotate(0deg)',
                },
                '100%': {
                  transform: 'rotate(-360deg)',
                },
              },
            }}
          >
            <Sync />
          </IconButton>
        </CustomTooltip>
      )}
    </Box>
  );
};

CustomEditor.defaultProps = {
  errors: [],
};

CustomEditor.propTypes = {
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      schemaPath: PropTypes.string.isRequired,
      instancePath: PropTypes.string,
      message: PropTypes.string,
    }),
  ),
  editable: PropTypes.bool,
  fetchable: PropTypes.bool,
  isFetching: PropTypes.bool,
  onChange: PropTypes.func,
  handleFetch: PropTypes.func,
  disabled: PropTypes.bool,
  validatorStrategy: PropTypes.string,
  validationSchema: PropTypes.object,
};

export default CustomEditor;
